'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'

// Neumorphic CTA shadow — mirrors the other landing cards and the Figma button
// (#3e4247 light / #1d2125 dark resolve from the neu-shadow tokens).
const CTA_SHADOW = '-10px -10px 30px var(--neu-shadow-light), 10px 10px 30px var(--neu-shadow-dark)'

// Blue glow lifted straight from the Figma headline.
const HEADLINE_TEXT_SHADOW = '0px 4px 41.4px #002137, 0px 2px 36px #002137'

export default function HeroSection() {
  const router = useRouter()

  return (
    <section className="relative min-h-[100svh] md:min-h-[820px] lg:min-h-[900px] overflow-hidden text-white">
      {/* Hero background — stadium photo + left→right dark scrim (Figma 4211:125155) */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/landing/hero-stadium.png')" }}
        />
        {/* Left→right gradient: dark on the left for text contrast, pitch visible on the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, #000000 0.87%, rgba(0,0,0,0.21) 17.64%, rgba(0,0,0,0.4) 97.74%)'
          }}
        />
        {/* Extra darkening on small screens (image crops to center) to keep copy legible */}
        <div className="absolute inset-0 bg-black/35 md:bg-transparent" />
      </div>

      {/* Content — left-aligned, vertically centered */}
      <div className="absolute inset-0 z-10 flex items-center">
        <div className="w-full px-6 sm:px-10 md:px-14 lg:px-20 xl:px-24 3xl:px-[83px]">
          {/* Content group — Figma "Group 427319766": 1009×314 (heights reproduce 1:1) */}
          <div className="max-w-[1009px]">
            {/* Impact 90px / line-height 110px (1.22) / letter-spacing 7px (0.078em) / weight 400 */}
            <h1
              className={cn(
                'uppercase text-white',
                'text-[34px] sm:text-[44px] md:text-[60px] lg:text-[68px] xl:text-[80px] 3xl:text-[90px]'
              )}
              style={{
                // Impact (Win/macOS system font) first to match the Figma design;
                // var(--font-anton) is the web-loaded fallback so Linux + every
                // phone still get a heavy condensed face instead of thin sans-serif.
                fontFamily: '"Impact", var(--font-anton), "Arial Black", sans-serif',
                fontWeight: 400,
                lineHeight: 1.22, // Figma 110px @ 90px
                letterSpacing: '0.078em', // Figma 7px @ 90px
                textShadow: HEADLINE_TEXT_SHADOW
              }}
            >
              {/* Text must live directly in <h1> (no child elements): the global
                  `* { font-family: Montserrat }` rule in globals.css hijacks any
                  descendant (e.g. a <span>), overriding the headline font. */}
              SIX LEAGUES
              <br />
              ONE DREAM TEAM.
            </h1>

            {/* Montserrat Medium 20px / line-height 24px (1.2) — 4px below headline (Figma) */}
            <p
              className={cn(
                'mt-1 font-montserrat font-medium text-white',
                'text-[12px] sm:text-sm md:text-base lg:text-lg xl:text-xl',
                'max-w-[887px]'
              )}
              style={{ lineHeight: 1.2, textShadow: HEADLINE_TEXT_SHADOW }} // Figma 24px @ 20px
            >
              WELCOME TO NEUROPLAY FANTASY CHALLENGE.
            </p>

            {/* Button — Figma 307×48, padding 21×34, gap to subtitle 18px */}
            <div className="mt-[18px]">
              <button
                type="button"
                onClick={() => router.push('/login')}
                className={cn(
                  'inline-flex h-11 md:h-12 items-center justify-center',
                  'rounded-[16px] px-7 sm:px-8 md:px-[34px]',
                  'bg-gradient-to-r from-neu-brand-from to-neu-brand-to',
                  'font-montserrat font-bold uppercase text-white',
                  'text-xs sm:text-sm md:text-base tracking-[0.15px]',
                  'transition-transform duration-300 hover:-translate-y-0.5 active:scale-[0.98]',
                  'focus:outline-none focus:ring-2 focus:ring-sky-300/60'
                )}
                style={{ boxShadow: CTA_SHADOW }}
              >
                BUILD YOUR DREAM TEAM
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
